/**
 * Theme: size guide unit toggle and the size finder.
 * Spec: product page companion sections 8 and 9.
 *
 * NO EASE, ANYWHERE. Body measurements are compared to the stored chart as-is.
 * Data model 1.4 rules out a single ease allowance, because ease "varies a lot:
 * a stretch jersey dress needs almost none, a relaxed overshirt needs a great
 * deal". Conversion happens on entry, by whoever builds the chart. If you are
 * ever tempted to subtract a few centimetres here, that is the bug.
 *
 * size_type is a data-quality gate handled in Liquid: this file only runs the
 * finder when the chart is matchable (size_type == 'body').
 *
 * Storage key `core_sizeprofile` (mirrored to `ats_sizeprofile` by the shim below),
 * holding the unit, the fit preference and the three measurements IN CENTIMETRES.
 * Separate from `core_wishlist`; do not merge.
 */
(function () {
  'use strict';

  var KEY = 'core_sizeprofile';

  /*
   * ⚠️ TEMPORARY MIGRATION SHIM, de-brand step 4d (15 September 2026).
   * REMOVE IT 90 DAYS AFTER THE DE-BRANDING DEPLOYS TO LIVE. The deploy date and
   * the resulting removal date are recorded in CLAUDE.md ("Storage key migration
   * shims", under Outstanding); until that entry carries a deploy date, the 90
   * days have not started.
   *
   * The live theme wrote this value under 'ats_sizeprofile'. On read, if the new
   * key is absent and the old key exists, the value is COPIED across and the old
   * key is kept. Every save then writes BOTH keys. Together that makes a rollback
   * to the old theme clean: it reads 'ats_sizeprofile' and finds the shopper's latest
   * state, not an empty value (changed before the first publish, 15 September
   * 2026; the shim used to delete the old key). When the new key already exists
   * the old one is never copied from again, so anything saved ON the old theme
   * during a rollback is not picked up when the new theme returns.
   * To remove: delete LEGACY_KEY, migrateLegacyKey and its single call in the
   * read function, and the LEGACY_KEY write in the save function below.
   */
  var LEGACY_KEY = 'ats_sizeprofile';

  function migrateLegacyKey() {
    var store = window.localStorage;
    if (store.getItem(KEY) !== null) return;
    var legacy = store.getItem(LEGACY_KEY);
    if (legacy === null) return;
    store.setItem(KEY, legacy);
    // COPY, never move: the legacy key stays so a rollback to the old theme still
    // finds the shopper's data.
  }
  var CM_PER_IN = 2.54;

  // Sane bounds. Without them a 999cm bust returns a confident XXL.
  var MIN_CM = 50;
  var MAX_CM = 200;

  /*
   * ⚠️ A CHART VALUE WITHIN EPS OF A MEASUREMENT COUNTS AS EQUAL.
   * An inch chart reaches us as centimetres rounded to two decimals, so a chest
   * of 44.1in arrives as 112.01cm while a shopper typing 44.1in becomes
   * 112.014cm. Without a tolerance that exact match fails >= and the shopper is
   * pushed a size up. 0.01cm is a tenth of a millimetre, below anything the chart
   * or the stepper can express, so it never changes a real centimetre answer.
   */
  var EPS = 0.01;

  /*
   * A chart value as a number, or NaN when the row does not carry it.
   * ⚠️ null IS NOT 0: Number(null) is 0 and isFinite(0) is true, so every read of
   * a row value goes through here rather than Number().
   */
  function num(raw) {
    if (raw === null || raw === undefined || raw === '') return NaN;
    var n = Number(raw);
    return isFinite(n) ? n : NaN;
  }

  function readProfile() {
    try {
      migrateLegacyKey();
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  function writeProfile(profile) {
    try {
      var value = JSON.stringify(profile);
      window.localStorage.setItem(KEY, value);
      window.localStorage.setItem(LEGACY_KEY, value); // shim: keep the rollback copy current
    } catch (error) {
      /* Private mode or full quota. The finder still works for this visit. */
    }
  }

  /**
   * Centimetres in, display value out. Whole centimetres, or inches to the
   * nearest HALF (not quarters: the chart is not that precise, and a shopper
   * reading a tape cannot be either).
   *
   * NO TRAILING-ZERO STRIPPING IS NEEDED HERE, and adding a toFixed would
   * introduce the very fault it looks like it prevents. JavaScript numbers
   * carry no trailing .0, so String(32) is "32" and String(31.5) is "31.5"
   * already. The ".0" problem is purely server-side, where the now-decimal
   * metafields render 90 as "90.0"; that is handled by `round` in
   * snippets/core-size-tools.liquid.
   */
  function toDisplay(cm, unit) {
    return unit === 'in' ? Math.round((cm / CM_PER_IN) * 2) / 2 : Math.round(cm);
  }

  function toCm(value, unit) {
    return unit === 'in' ? value * CM_PER_IN : value;
  }

  /**
   * The smallest row that actually FITS one measurement.
   *
   * ROUNDS UP, NEVER DOWN. This replaced a nearest-by-distance pick, which
   * chose whichever chart value was closer in absolute terms and therefore
   * rounded down whenever a measurement sat just below the midpoint between two
   * sizes. On the sample dress, M bust 88 and L bust 93, a bust of 90 came back
   * as M: nearer to 88, but 90 does not go into an 88 garment. That contradicts
   * the locked rule, which is to match the body to the chart and let the
   * largest governing measurement win. You can take in a waist. You cannot add
   * to a bust.
   *
   * An exact match takes that size rather than the one above, because `>=`
   * includes equality: a bust of 88 is M, not L.
   *
   * Written by VALUE rather than by position, so a chart row out of order
   * cannot produce a silently wrong pick. The index it returns is still
   * positional, because the caller's largest-governing step and the fit
   * preference's band lookup both walk the rows by index.
   *
   * BOUNDARIES. A measurement above every row has no size that fits, so it
   * falls back to the largest row, and recommend()'s existing `overTop` check
   * adds the "largest size in this piece" note. A measurement at or below the
   * smallest row is already satisfied by row 0, so it maps there and `under-
   * Bottom` adds the matching note. Neither case is special-cased here.
   */
  function fittingIndex(rows, field, valueCm) {
    var fitIdx = -1;
    var fitValue = Infinity;
    var topIdx = -1;
    var topValue = -Infinity;
    for (var i = 0; i < rows.length; i++) {
      // A row that does not carry this measurement is skipped outright (see num).
      var target = num(rows[i][field]);
      if (!isFinite(target)) continue;
      if (target > topValue) {
        topValue = target;
        topIdx = i;
      }
      // Strictly smaller, so where two rows carry the same value the smaller
      // size wins: the smallest size that fits, not merely one that fits.
      if (target + EPS >= valueCm && target < fitValue) {
        fitValue = target;
        fitIdx = i;
      }
    }
    return fitIdx >= 0 ? fitIdx : topIdx;
  }

  function initUnits(scope, onChange) {
    var buttons = scope.querySelectorAll('[data-unit]');
    buttons.forEach(function (button) {
      button.addEventListener('click', function () {
        buttons.forEach(function (b) {
          b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
        });
        onChange(button.dataset.unit);
      });
    });
  }

  function initGuide(root, unitState) {
    var table = root.querySelector('.core-sizetable');
    if (!table) return;

    /*
     * ⚠️ THE ENTERED UNIT SHOWS THE LISTER'S OWN FIGURE; ONLY THE DERIVED UNIT
     * ROUNDS. data-entered names the unit the chart was typed in, and data-raw
     * carries that exact figure already formatted by Liquid, so it is inserted
     * as-is. The other unit is rounded from data-cm by toDisplay, exactly as the
     * server rounds it, which is what makes CM -> IN -> CM land back on identical
     * text. Markup without data-entered falls back to rounding both, the old rule.
     */
    var entered = table.getAttribute('data-entered');
    function cellValue(cell, rawAttr, cmAttr, unit) {
      var raw = cell.getAttribute(rawAttr);
      if (entered && unit === entered && raw !== null && raw !== '') return raw;
      var cm = Number(cell.getAttribute(cmAttr));
      return isFinite(cm) ? String(toDisplay(cm, unit)) : null;
    }

    initUnits(root, function (unit) {
      unitState.guide = unit;
      // Single-value cells carry data-cm, exactly as they always have.
      table.querySelectorAll('[data-cm]').forEach(function (cell) {
        var t = cellValue(cell, 'data-raw', 'data-cm', unit);
        if (t !== null) cell.textContent = t;
      });
      // Range cells carry both ends and the whole "84 to 88" string is rebuilt,
      // because one stored number could not reproduce it. See snippets/core-size-cell.liquid.
      table.querySelectorAll('[data-cm-min][data-cm-max]').forEach(function (cell) {
        var lo = cellValue(cell, 'data-raw-min', 'data-cm-min', unit);
        var hi = cellValue(cell, 'data-raw-max', 'data-cm-max', unit);
        if (lo === null || hi === null) return;
        cell.textContent = lo + ' to ' + hi;
      });
    });
  }

  function initFinder(root, chart, pdp) {
    var form = root.querySelector('[data-finder]');
    if (!form) return;

    var rows = chart.rows || [];
    var chartFields = Array.isArray(chart.fields) && chart.fields.length
      ? chart.fields
      : ['bust', 'waist', 'hip'];
    var required = Math.min(2, chartFields.length);
    var singleField = chartFields.length === 1 ? chartFields[0] : null;
    var resultEl = form.querySelector('[data-finder-result]');
    var inputs = Array.prototype.slice.call(form.querySelectorAll('[data-measure]'));
    var fitButtons = Array.prototype.slice.call(form.querySelectorAll('[data-fit]'));

    var profile = readProfile();
    var unit = profile.unit === 'in' ? 'in' : 'cm';
    var fit = profile.fit || 'regular';

    // Prefill from browser memory, converting the stored centimetres.
    inputs.forEach(function (input) {
      var cm = profile[input.dataset.measure];
      if (typeof cm === 'number' && isFinite(cm)) {
        input.value = String(toDisplay(cm, unit));
      }
    });
    form.querySelectorAll('[data-unit]').forEach(function (b) {
      b.setAttribute('aria-pressed', b.dataset.unit === unit ? 'true' : 'false');
    });
    fitButtons.forEach(function (b) {
      b.setAttribute('aria-pressed', b.dataset.fit === fit ? 'true' : 'false');
    });

    /** @returns {{bust?:number, waist?:number, hip?:number}} in centimetres. */
    function measurementsCm() {
      var out = {};
      inputs.forEach(function (input) {
        var raw = parseFloat(input.value);
        if (!isFinite(raw)) return;
        var cm = toCm(raw, unit);
        if (cm < MIN_CM || cm > MAX_CM) return;
        out[input.dataset.measure] = cm;
      });
      return out;
    }

    function outOfBounds() {
      return inputs.some(function (input) {
        var raw = parseFloat(input.value);
        if (!isFinite(raw)) return false;
        var cm = toCm(raw, unit);
        return cm < MIN_CM || cm > MAX_CM;
      });
    }

    function label(field) {
      return field === 'bust' ? String(chart.measure_label || 'Bust').toLowerCase() : field;
    }

    /**
     * Render the concept's result box. The MATCHING LOGIC IS UNTOUCHED; this
     * only changes how its output is presented, from one text node to the
     * concept's eyebrow / big size / reason / confirm structure.
     */
    function showError(message) {
      resultEl.className = 'core-sf__result is-shown';
      resultEl.innerHTML = '';
      var p = document.createElement('p');
      p.className = 'core-sf__error';
      p.textContent = message;
      resultEl.appendChild(p);
    }

    function clearResult() {
      resultEl.className = 'core-sf__result';
      resultEl.innerHTML = '';
    }

    function showResult(size, eyebrow, why, confirm) {
      resultEl.className = 'core-sf__result is-shown';
      resultEl.innerHTML = '';
      var e = document.createElement('div');
      e.className = 'core-sf__eyebrow';
      e.textContent = eyebrow;
      var s2 = document.createElement('div');
      s2.className = 'core-sf__size';
      s2.textContent = size;
      var w = document.createElement('p');
      w.className = 'core-sf__why';
      w.textContent = why;
      resultEl.appendChild(e);
      resultEl.appendChild(s2);
      resultEl.appendChild(w);
      if (confirm) {
        var cf = document.createElement('div');
        cf.className = 'core-sf__confirm';
        cf.textContent = confirm;
        resultEl.appendChild(cf);
      }
    }

    function recommend() {
      if (!rows.length) return;

      if (outOfBounds()) {
        showError('That looks outside our range. Please check the figure and try again.');
        return;
      }

      var given = measurementsCm();
      var fields = Object.keys(given);

      // REQUIRED INPUTS = the lesser of two and the chart's MATCHABLE COLUMNS.
      //
      // Two, where the chart carries two or more of bust, waist and hip: any two
      // will do, no pair is privileged, and one measurement can only ever agree
      // with itself, so the largest-governing rule would have nothing to
      // arbitrate. (This overrides the companion's "one measurement is enough".)
      //
      // One, where the chart carries a single matchable column. Liquid only
      // allows that path when the column is BUST (see the pre-pass in
      // snippets/core-size-tools.liquid), and init() refuses any other single
      // column too, so a waist-only or hip-only chart never reaches here.
      if (!fields.length) {
        clearResult();
        return;
      }
      if (fields.length < required) {
        showError('Add a second measurement and we will suggest a size.');
        return;
      }

      var picks = [];
      var maxIdx = -1;
      fields.forEach(function (field) {
        var idx = fittingIndex(rows, field, given[field]);
        if (idx < 0) return;
        picks.push({ field: field, idx: idx, size: rows[idx].size });
        if (idx > maxIdx) maxIdx = idx;
      });
      if (maxIdx < 0) {
        clearResult();
        return;
      }

      // Take the LARGEST, so nothing pulls.
      var chosenIdx = maxIdx;
      var note = '';

      // ---- FIT PREFERENCE, BOTH DIRECTIONS MEASUREMENT-GUARDED ----
      //
      // This LAYERS ON the locked match above; it never replaces it. The base
      // size is always the largest-governing result, and both adjustments are
      // real chart checks, never an ease constant or a blind offset.
      //
      // The governing field is the one that produced the base size. It is the
      // measurement the garment has to clear, so it is the one both guards test.
      var governing = picks.filter(function (p) { return p.idx === maxIdx; })[0];
      var govField = governing ? governing.field : fields[0];

      if (fit === 'fitted' && chosenIdx > 0) {
        // GUARD DOWN: drop only if the smaller size still clears EVERY
        // measurement given. Otherwise stay and say which one stopped it.
        var smaller = chosenIdx - 1;
        var tight = null;
        fields.forEach(function (field) {
          if (tight) return;
          var target = num(rows[smaller][field]);
          if (isFinite(target) && given[field] > target + EPS) tight = field;
        });
        if (!tight) {
          chosenIdx = smaller;
          note = ' Sized down for a closer fit, which your measurements still allow.';
        } else {
          note = ' A smaller size would be tight on your ' + label(tight) +
                 ', so we have kept you at ' + rows[chosenIdx].size + '.';
        }
      } else if (fit === 'relaxed' && chosenIdx < rows.length - 1) {
        // GUARD UP. This is the deliberate deviation from the concept, whose
        // Relaxed is an unguarded +1 that can hand someone a size too big
        // purely because they tapped a preference.
        //
        // THE TEST: is the shopper in the upper half of their size's band on the
        // governing measurement? Every size band runs from the size below's
        // value up to this size's value. Sitting high in that band means the
        // next size up is a natural bit of room. Sitting low means they are
        // already comfortable here and a bump would be most of two grades of
        // slack. The midpoint is derived from the chart's own grading, so no
        // invented tolerance is involved.
        //
        // THIS GUARD ONLY STARTED WORKING WHEN THE BASE PICK BEGAN ROUNDING UP.
        // Under the old nearest-by-distance pick the governing measurement was
        // by definition closer to bandTop than to bandBottom, which is the same
        // statement as `given >= midpoint`, so the test could never fail and the
        // decline below was unreachable at every size except the smallest (row 0
        // escaped only because its band is synthesised rather than read). Rounding
        // up puts the measurement anywhere in its true band, so both outcomes are
        // now live. Do not "simplify" this back into an unguarded +1.
        var bandTop = num(rows[chosenIdx][govField]);
        var bandBottom = chosenIdx > 0
          ? num(rows[chosenIdx - 1][govField])
          // No band below: mirror this chart's own grade step upward.
          : bandTop - (num(rows[chosenIdx + 1][govField]) - bandTop);
        var midpoint = (bandBottom + bandTop) / 2;

        if (isFinite(midpoint) && given[govField] >= midpoint) {
          chosenIdx = chosenIdx + 1;
          note = ' Sized up for a relaxed fit, which your measurements still suit.';
        } else {
          note = ' A larger size would be loose on your ' + label(govField) +
                 ', so we have kept you at ' + rows[chosenIdx].size + '.';
        }
      }

      var size = rows[chosenIdx].size;

      // Beyond the chart at either end. Recommend the boundary size and say so
      // factually: point at the measurements, never editorialise about the
      // shopper's body, never imply the garment will not fit them.
      var boundary = '';
      var last = rows.length - 1;
      var overTop = fields.some(function (f) {
        var top = num(rows[last][f]);
        return isFinite(top) && given[f] > top + EPS;
      });
      var underBottom = fields.some(function (f) {
        var bottom = num(rows[0][f]);
        return isFinite(bottom) && given[f] < bottom - EPS;
      });
      if (overTop) {
        boundary = ' ' + rows[last].size + ' is the largest size in this piece. Its measurements are in the size guide, worth checking against your own.';
      } else if (underBottom) {
        boundary = ' ' + rows[0].size + ' is the smallest size in this piece. Its measurements are in the size guide, worth checking against your own.';
      }

      // Show the reasoning, not a bare verdict.
      var spanned = picks
        .map(function (p) { return label(p.field) + ' ' + p.size; })
        .join(', ');
      var reason = picks.length > 1 && picks.some(function (p) { return p.size !== picks[0].size; })
        ? ' Your measurements spanned ' + spanned + ', so we have gone with the larger to avoid anything pulling.'
        : '';

      // SIZED ON ONE MEASUREMENT: say so, rather than let one figure read like the
      // full comparison. Only reachable for a bust-only chart.
      var lead = singleField
        ? 'Based on your measurement, we suggest ' + size + '.'
        : 'Based on your measurements, we suggest ' + size + '.';
      var only = singleField
        ? ' This piece only lists ' + label(singleField) + ' measurements, so we have sized on your ' + label(singleField) + ' alone.'
        : '';
      var why = (lead + only + reason + note + boundary).trim();

      // Auto-select on the product page, and report sold-out honestly.
      var applied = applyToProduct(size, rows[chosenIdx].source_label);
      var confirm = '';
      if (applied === 'soldout') {
        confirm = 'Size ' + size + ' is sold out in this colour. Try another colour, or check back soon.';
      } else if (applied === 'applied') {
        confirm = 'Selected on the product page for you.';
      }

      showResult(size, applied === 'applied' ? 'Selected for you' : 'Your size', why, confirm);
    }

    /**
     * Apply the recommendation to the size row.
     *
     * ALWAYS reports the true best-fit size. An earlier version fell back to the
     * nearest in-stock size and selected that; it was rejected because a bumped
     * size is less flattering, may not fit, and drives returns on exactly the
     * products selling fastest. If the size is unavailable we select NOTHING.
     *
     * ⚠️ THE CHART AND THE BUTTONS ARE TWO DIFFERENT LISTS. The buttons come from
     * the product's Size option; the row label comes from the size_row metaobject,
     * and after a relabel the two can disagree. So: match the row's label to an
     * option value, fall back to its source_label (what the row was called before
     * it was relabelled), and if NEITHER matches, select nothing and let the
     * result text state the size. Selecting the wrong variant is far worse than
     * selecting none, and this is the same reasoning as never bumping to an
     * in-stock size.
     *
     * @returns {'applied'|'soldout'|'missing'}
     */
    function applyToProduct(size, sourceLabel) {
      if (!pdp) return 'missing';
      var button = find(size) || find(sourceLabel);
      if (!button) return 'missing';
      if (button.getAttribute('data-available') === 'false') return 'soldout';
      button.click();
      return 'applied';

      function find(value) {
        if (value === null || value === undefined || value === '') return null;
        return pdp.querySelector('[data-size-value="' + String(value).replace(/"/g, '') + '"]');
      }
    }

    // The form may now carry only some of the three fields, so a save overwrites
    // only the ones on this form. Otherwise a bust-only product would wipe the
    // waist and hip a shopper saved on another product.
    function persist() {
      var given = measurementsCm();
      var previous = readProfile();
      var next = { unit: unit, fit: fit, bust: previous.bust, waist: previous.waist, hip: previous.hip };
      inputs.forEach(function (input) {
        next[input.dataset.measure] = given[input.dataset.measure];
      });
      writeProfile(next);
    }

    inputs.forEach(function (input) {
      input.addEventListener('input', function () {
        recommend();
        persist();
      });
    });

    // Custom steppers: 1 in centimetres, 0.5 in inches, starting from a sensible
    // mid-range value when the field is empty.
    form.querySelectorAll('[data-step]').forEach(function (button) {
      button.addEventListener('click', function () {
        // Walk OUT until an ancestor contains the field, rather than assuming
        // the button's immediate parent does. The concept nests the chevrons in
        // their own column inside the input wrapper, so parentElement is the
        // chevron stack and holds no input; that assumption silently broke the
        // steppers when the markup was rebuilt. This survives renesting.
        var input = null;
        var scope = button.parentElement;
        while (scope && !input) {
          input = scope.querySelector('[data-measure]');
          scope = scope.parentElement;
        }
        if (!input) return;
        var step = unit === 'in' ? 0.5 : 1;
        var current = parseFloat(input.value);
        if (!isFinite(current)) current = unit === 'in' ? 35 : 90;
        else current += button.dataset.step === 'up' ? step : -step;
        input.value = String(Math.round(current * 2) / 2);
        recommend();
        persist();
      });
    });

    fitButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        fit = button.dataset.fit;
        fitButtons.forEach(function (b) {
          b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
        });
        recommend();
        persist();
      });
    });

    var goButton = form.querySelector('[data-finder-go]');
    if (goButton) {
      goButton.addEventListener('click', function () {
        recommend();
      });
    }

    initUnits(form, function (next) {
      inputs.forEach(function (input) {
        // The placeholder IS the unit, so it moves on every toggle, including on
        // the empty boxes, which is the whole point: it tells the shopper what
        // the next figure should be measured in.
        input.placeholder = next;
        // Convert the values already typed rather than clearing them.
        var raw = parseFloat(input.value);
        if (!isFinite(raw)) return;
        var cm = toCm(raw, unit);
        input.value = String(toDisplay(cm, next));
      });
      unit = next;
      recommend();
      persist();
    });

    // Reopening shows the previous suggestion WITHOUT overriding a size the
    // shopper has since picked manually, so this only runs on open, and
    // applyToProduct is what would change the selection. We show the text only.
    var drawer = root.closest('theme-drawer');
    if (drawer) {
      drawer.addEventListener('theme-drawer:open', function () {
        if (resultEl.textContent) return;
        recommend();
      });
    }
  }

  function init() {
    var json = document.querySelector('[data-core-sizechart]');
    if (!json) return;

    var chart;
    try {
      chart = JSON.parse(json.textContent);
    } catch (error) {
      return;
    }
    if (!chart) return;

    var pdp = document.querySelector('.core-pdp');
    var unitState = { guide: 'cm' };

    // ⚠️ THE GUIDE'S UNIT TOGGLE DOES NOT DEPEND ON THE FINDER'S ROWS. A chart can
    // carry only length or shoulder figures, so its finder rows are empty while
    // its table is full. An earlier version returned before this line whenever
    // the rows were empty, which left that table's CM/IN toggle dead.
    var guide = document.getElementById('core-size-guide');
    if (guide) initGuide(guide, unitState);

    if (!Array.isArray(chart.rows) || !chart.rows.length) return;

    var finder = document.getElementById('core-size-finder');
    // Liquid decides recommendable; the single-column check is repeated here so
    // a hand-edited or cached chart can never size on waist or hip alone.
    var f = Array.isArray(chart.fields) ? chart.fields : null;
    var safe = !f || f.length >= 2 || (f.length === 1 && f[0] === 'bust');
    if (finder && chart.matchable && chart.recommendable !== false && safe) initFinder(finder, chart, pdp);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
